<template>
  <modal
    elem-id="modal_crud_cliente"
    :show="isVisible"
    :title="t('Cliente')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving"
    :save-label="t('Salvar')"
    :close-label="t('Fechar')"
    size="compactDesktop"
    @close="handleClose"
    @dismiss="handleClose">
      
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <div data-testid="cliente-modal">
        <!-- Ações do cliente: uma linha só, alinhadas à direita. Antes eram
             dois contêineres empilhados — um com justify-end e o outro sem
             estilo nenhum — o que deixava os botões em alturas e lados
             diferentes. -->
        <div class="cliente-top-actions" v-if="clienteId > 0">
          <Button
            :label="t('Enviar link de pré-cadastro')"
            icon="pi pi-whatsapp"
            severity="success"
            data-testid="cliente-enviar-precadastro-btn"
            @click="handleEnviarPreCadastro"
          />
          <Button
            v-if="clienteStatus !== 2"
            :label="t('Arquivar cliente')"
            icon="pi pi-ban"
            severity="danger"
            data-testid="cliente-archive-btn"
            @click="handleArchive"
          />
        </div>

        <!-- Version 1 Fields -->
        <div v-if="usuarioVersao == 1 && clienteId > 0" class="version-1-fields">
          <input-date-time
            v-if="orcamentosEnabled"
            v-model="clienteData.cliente_data_ultimo_orcamento"
            elem-id="cliente_data_ultimo_orcamento"
            :label="t('Último orçamento')"
            :disabled="true"
            data-testid="cliente-ultimo-orcamento-input">
          </input-date-time>

          <input-date-time
            v-if="compromissosEnabled"
            v-model="clienteData.cliente_data_ultimo_compromisso"
            elem-id="cliente_data_ultimo_compromisso"
            :label="t('Último compromisso')"
            :disabled="true"
            data-testid="cliente-ultimo-compromisso-input">
          </input-date-time>

          <input-number
            v-if="compromissosEnabled"
            v-model="clienteData.cliente_qtd_compromissos"
            elem-id="cliente_qtd_compromissos"
            :label="t('Quantidade de compromissos')"
            :disabled="true"
            data-testid="cliente-qtd-compromissos-input">
          </input-number>

          <input-date-time
            v-model="clienteData.cliente_data_ultimo_contato_fidelizacao"
            elem-id="cliente_data_ultimo_contato_fidelizacao"
            :label="t('Último contato')"
            :disabled="true"
            data-testid="cliente-ultimo-contato-input">
          </input-date-time>

        </div>
        
        <!-- Tabs -->
        <Tabs v-if="clienteId > 0" v-model:value="activeTabIndex" :scrollable="false">
          <TabList>
            <Tab :value="0" data-testid="cliente-tab-editar">{{ t('Editar') }}</Tab>
            <Tab v-if="orcamentosEnabled" :value="1" data-testid="cliente-tab-orcamentos">{{ t('Orçamentos') }}</Tab>
            <Tab v-if="usuarioVersao == 1" :value="2" data-testid="cliente-tab-contatos">{{ t('Contatos') }}</Tab>
            <Tab v-if="servicosEnabled && isManagerOrAbove" :value="3" data-testid="cliente-tab-servicos">{{ t('Ordens de Serviço') }}</Tab>
            <Tab v-if="invoicesEnabled && isManagerOrAbove" :value="4" data-testid="cliente-tab-faturas">{{ t('Faturas') }}</Tab>
            <!-- Gated by the `portalcliente` feature: a tenant without a
                 customers portal (doutor, omb) lists it in AWRENV_SISTEMAAPI_DISABLED_FEATURES
                 and gets no tab at all — and, because the panel lives inside it,
                 no status call either. -->
            <Tab v-if="portalClienteEnabled" :value="5" data-testid="cliente-tab-portal">{{ t('Portal do cliente') }}</Tab>
            <Tab v-if="combosEnabled && canUseCombos" :value="6" data-testid="cliente-tab-combos">{{ t('Combos') }}</Tab>
          </TabList>
          <TabPanels>
            <!-- Edit Tab -->
            <TabPanel :value="0">
              <cliente-form
                ref="clienteFormRef"
                :cliente-id="clienteId"
                :usuario-versao="usuarioVersao"
                :prefill="prefillData"
                @save-success="handleSaveSuccess"
                @client-loaded="handleClientLoaded">
              </cliente-form>
            </TabPanel>

            <!-- Orcamentos Tab -->
            <TabPanel v-if="orcamentosEnabled" :value="1">
              <orcamentos-do-cliente-table
                ref="orcamentosTabRef"
                :cliente-id="clienteId">
              </orcamentos-do-cliente-table>
            </TabPanel>
            
            <!-- Contatos Tab (Version 1 only) -->
            <TabPanel v-if="usuarioVersao == 1" :value="2">
              <contato-fidelizacao-table
                ref="contatosTabRef"
                :cliente-id="clienteId"
                :cliente-nome="clienteData.cliente_nome"
                :franquia-id="clienteData.cliente_franquia_id || 0"
                :franquia-nome="clienteData.cliente_franquia_nome || ''">
              </contato-fidelizacao-table>
            </TabPanel>

            <!-- SO Orders Tab -->
            <TabPanel v-if="servicosEnabled && isManagerOrAbove" :value="3">
              <SOOrdersDoClienteTable
                ref="soOrdersTabRef"
                :cliente-id="clienteId">
              </SOOrdersDoClienteTable>
            </TabPanel>

            <!-- Invoices Tab -->
            <TabPanel v-if="invoicesEnabled && isManagerOrAbove" :value="4">
              <invoices-do-cliente-table
                ref="invoicesTabRef"
                :cliente-id="clienteId"
                :franquia-id="clienteData.cliente_franquia_id || 0">
              </invoices-do-cliente-table>
            </TabPanel>
            <!-- Portal do cliente (my.newshoes.com) — provisioning and messaging
                 for one customer, in the one place staff look for both.
                 The `portalcliente` gate is on the Tab itself, so a tenant
                 without a portal gets no tab, no panel and no status call. -->
            <TabPanel v-if="portalClienteEnabled" :value="5">
            <ClientePortalTab
              :cliente-id="clienteId"
              :state="portalState"
              :account="portalAccount"
              :credential="portalCredential"
              :failure-message="portalFailureMessage"
              :reachability-message="portalReachabilityMessage"
              :is-loading="portalIsLoading"
              :is-creating="portalIsCreating"
              :is-resetting="portalIsResetting"
              :pode-resetar="podeResetarAcessoPortal"
              @create="createAcessoPortal(clienteId)"
              @retry="loadAcessoPortal(clienteId)"
              @reset="confirmarResetAcessoPortal"
              @notify="(texto?: string) => abrirNotificacaoModal(texto)"
            />
            </TabPanel>

            <!-- Combos pré-pagos (NS 2026-0090). O painel monta com o modal; a
                 consulta só sai ao abrir a aba (watch de activeTabIndex). -->
            <TabPanel v-if="combosEnabled && canUseCombos" :value="6">
              <ClienteCombosTab ref="combosTabRef" :cliente-id="clienteId" />
            </TabPanel>

          </TabPanels>
        </Tabs>
        
        <!-- No tabs for new client -->
        <div v-else>
          <cliente-form
            ref="clienteFormRef"
            :cliente-id="clienteId"
            :usuario-versao="usuarioVersao"
            :prefill="prefillData"
            v-model:pending-observation="pendingObservation"
            @save-success="handleSaveSuccess">
          </cliente-form>
        </div>
        <!-- Observations. Cliente novo (clienteId === 0) ainda não tem dono para a
             ocorrência: o editor fica inline e o texto vai no ref pendingObservation,
             gravado pelo ClienteForm logo depois do POST clientes/save (mesmo padrão
             do OrcamentosForm). Cliente existente usa o modal central + tabela. -->
        <box v-if="ocorrenciasEnabled" :title="t('Observações do cliente')" type="primary">
          <occurrences-page
            ref="occurrencesPageRef"
            owner="cliente"
            :owner-id="clienteId"
            :mode="clienteId > 0 ? 'modal' : 'inline'"
            v-model="pendingObservation"
            :show-table="clienteId > 0"
            :show-inline-save-btn="false"
            :allow-delete="true"
            :add-label="t('Adicionar observação')"
            :modal-title="t('Nova ocorrência')"
            :description-label="t('Descrição')"
            :close-label="t('Fechar')"
            :save-label="t('Salvar observação')">
          </occurrences-page>
        </box>
    
      <!-- ContatoFidelizacaoModal is centralized in ModalManager.vue - opened via eventBus -->
      <!-- ClienteArchiveModal is centralized in ModalManager.vue - opened via eventBus -->
      <!-- OrcamentosModal is centralized in ModalManager.vue - opened via eventBus -->
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ClienteForm from './ClienteForm.vue';
import ClientePortalTab from './ClientePortalTab.vue';
import ClienteCombosTab from './ClienteCombosTab.vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { permissionsService } from '../../services/permissionsService';
import { config } from '../../services/configService';
import { useUserPermissions } from '../../composables/usePermissions';
import { useClientePreCadastro } from '../../composables/useClientePreCadastro';
import { useClienteAcessoPortal } from '../../composables/useClienteAcessoPortal';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { useConfirmAction } from '../../composables/useConfirmAction';

const { isManagerOrAbove, canUseCombos } = useUserPermissions();
const { offerPreCadastroLink } = useClientePreCadastro();
const { formatDateTime } = useDateFormatter();
const { confirmAction } = useConfirmAction();

const {
  state: portalState,
  isLoading: portalIsLoading,
  isCreating: portalIsCreating,
  isResetting: portalIsResetting,
  account: portalAccount,
  credential: portalCredential,
  failure: portalFailure,
  pushReachability: portalPushReachability,
  loadStatus: loadAcessoPortal,
  createAccess: createAcessoPortal,
  // "XxxAPI" suffix, same convention as DespesasModal.vue's cancelarDespesaAPI:
  // `resetAcessoPortal` below is already taken by the composable's state-wipe `reset()`.
  resetAccess: resetarAcessoPortalAPI,
  reset: resetAcessoPortal,
} = useClienteAcessoPortal();

/**
 * Only offered once there is a credential to invalidate — never for `none`
 * (nothing exists yet) and never for `blocked`/`disabled` (a reset does not fix
 * either; see the account_already_active / account_disabled arms in
 * sendPortalIntegrationError()). Read off the account's raw `status` rather than
 * the coarser `portalState`: the composable's state machine folds
 * `credential_expired` into `other` (a status it does not special-case), but a
 * reset is exactly what an expired-credential account needs.
 */
const podeResetarAcessoPortal = computed<boolean>(() => {
  const status = portalAccount.value?.status;
  return status === 'pending_activation' || status === 'active' || status === 'credential_expired';
});

/**
 * A reset invalidates whatever the customer is currently using to sign in —
 * the confirm dialog exists precisely so staff cannot fire it by accident.
 * Only on confirm does resetarAcessoPortalAPI() run; the new code is then shown
 * by the existing credential InlineMessage, same as after a create.
 */
function confirmarResetAcessoPortal(): void {
  confirmAction({
    message: t(
      'Isto desconecta o cliente de todos os aparelhos e gera um novo código de ativação — os 6 primeiros dígitos do documento. Faça apenas com o cliente presente.',
    ),
    header: t('Resetar acesso ao portal?'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Resetar acesso'),
    rejectLabel: t('Cancelar'),
    onAccept: () => {
      resetarAcessoPortalAPI(clienteId.value);
    },
  });
}

/**
 * The backend phrases each rejection next to the rule that produced it, so its
 * sentence wins whenever there is one. The status-keyed fallbacks cover the
 * calls that never reached the controller (gateway/timeout, empty envelope);
 * 429 is handled first because a rate limiter answers with a machine code and
 * "tente de novo" is the only useful thing to say about it.
 */
const portalFailureMessage = computed<string>(() => {
  const falha = portalFailure.value;
  if (!falha) return '';
  if (falha.status === 429) {
    return t('Muitas tentativas em pouco tempo. Aguarde alguns instantes e tente de novo.');
  }
  if (falha.detail) return falha.detail;
  if (falha.status === 422) {
    return t('Este cliente precisa de um CPF/CNPJ no cadastro para ter acesso ao portal.');
  }
  if (falha.status === 503) {
    return t('O acesso ao portal do cliente está desligado neste ambiente.');
  }
  return t('Não foi possível concluir a ação no portal do cliente. Tente de novo em alguns instantes.');
});

/**
 * ONE line, not a branch stitched onto each account-state arm above: only
 * `inbox_only` and `reachable` say anything about devices — every other
 * reachability answer (`no_account`, `not_activated`, `undetermined`) is
 * either already covered by the account-state elements or, for
 * `undetermined`, must say NOTHING about devices — see the null-safety rule
 * on `pushDevices`/`pushReachability` in useClienteAcessoPortal.ts. Vocabulary
 * matches PDVEntregasNotifyModal.vue's "no_devices" outcome — staff see both
 * screens and the same fact must not be worded two different ways.
 */
const portalReachabilityMessage = computed<string>(() => {
  switch (portalPushReachability.value) {
    case 'inbox_only':
      return t('Só chega na caixa de entrada — o cliente não ativou avisos no celular.');
    case 'reachable':
      return t('Recebe avisos no celular.');
    default:
      return '';
  }
});

// Reabre o popup de envio do link de pré-cadastro (ex.: se fechou sem enviar).
// offerPreCadastroLink só abre quando o perfil está incompleto; caso contrário
// avisa que não há link a enviar.
async function handleEnviarPreCadastro(): Promise<void> {
  const opened = await offerPreCadastroLink(clienteId.value);
  if (!opened) {
    eventBus.emit('globalMessage.info', t('Cadastro já está completo — não há link de pré-cadastro a enviar.'));
  }
}

const orcamentosEnabled = config.isFeatureEnabled('orcamentos');
const ocorrenciasEnabled = config.isFeatureEnabled('ocorrencias');
const compromissosEnabled = config.isFeatureEnabled('compromissos');
const servicosEnabled = config.isFeatureEnabled('servicos');
const invoicesEnabled = config.isFeatureEnabled('invoices');
// Tenants without a customers portal (doutor, omb) list `portalcliente` in
// AWRENV_SISTEMAAPI_DISABLED_FEATURES. This gates the READ too, not just the button: with it
// off the modal must not call clientes/acesso-portal at all, so a tenant that
// has no portal never generates traffic towards one. The backend kill switch
// (AWRENV_SISTEMAAPI_PORTAL_PROVISIONING_ENABLED) is the second, authoritative
// gate. See skill `my-newshoes-portal-integration`.
const portalClienteEnabled = config.isFeatureEnabled('portalcliente');
const combosEnabled = config.isFeatureEnabled('combos');

/**
 * Opened through the eventBus rather than a ref, per the modal convention: the
 * notification modal is registered once in ModalManager and is never imported
 * by this component.
 */
/**
 * `texto` chega quando o envio partiu do mini-form da aba do portal (modelo já
 * substituído ou texto livre). O modal abre com a mensagem pronta em vez de em
 * branco — sem isso o atendente escreveria a mesma coisa duas vezes.
 */
function abrirNotificacaoModal(texto?: string): void {
  eventBus.emit('modal.cliente-notificacao.open', {
    id: Number(clienteId.value),
    mensagem: texto ?? '',
  });
}

type ClienteId = number | string;

interface ClienteFormInstance {
  save: () => Promise<void>;
}

interface OrcamentosTabInstance {
  loadData: () => void;
}

interface SOOrdersTabInstance {
  loadData: () => void;
}

interface InvoicesTabInstance {
  loadData: () => void;
}

interface ClientLoadedData {
  cliente_status?: string | number;
  cliente_nome?: string;
  cliente_franquia_id?: number | null;
  franquia?: { Name?: string };
  cliente_data_ultimo_orcamento?: string;
  cliente_data_ultimo_compromisso?: string;
  cliente_qtd_compromissos?: number;
  cliente_data_ultimo_contato_fidelizacao?: string;
}

interface ModalOpenPayload {
  id?: ClienteId;
  prefillPhone?: string;
  prefillName?: string;
  /** Opens straight to a tab instead of the default 'editar' — currently only
   *  'portal' (e.g. PDVEntregasNotifyModal's "Criar acesso" action, from the
   *  Guardado/no_portal_account outcome, plano-gatilhos-notificacao.md §2). */
  tab?: 'portal';
}

interface PrefillData {
  phone: string;
  name: string;
}

interface ClienteModalProps {
  clienteId?: number;
  show?: boolean;
}

const props = withDefaults(defineProps<ClienteModalProps>(), {
  clienteId: 0,
  show: false
});

const emit = defineEmits<{
  close: [];
  'save-success': [];
}>();

const { t } = useI18n();

const clienteFormRef = ref<ClienteFormInstance | null>(null);
const orcamentosTabRef = ref<OrcamentosTabInstance | null>(null);
const contatosTabRef = ref<unknown>(null);
const soOrdersTabRef = ref<SOOrdersTabInstance | null>(null);
const invoicesTabRef = ref<InvoicesTabInstance | null>(null);
const combosTabRef = ref<{ loadData: () => Promise<void> } | null>(null);
const occurrencesPageRef = ref<unknown>(null);
const activeTab = ref<'editar' | 'orcamentos' | 'contatos' | 'so-orders' | 'invoices'>('editar');
const activeTabIndex = ref(0);
const isSaving = ref(false);

const isVisible = ref(false);
const modalClienteId = ref(0);
const prefillData = ref<PrefillData | null>(null);
const pendingObservation = ref('');

const clienteData = ref({
  cliente_nome: '',
  cliente_franquia_id: null as number | null,
  cliente_franquia_nome: '',
  cliente_data_ultimo_orcamento: '',
  cliente_data_ultimo_compromisso: '',
  cliente_qtd_compromissos: 0,
  cliente_data_ultimo_contato_fidelizacao: ''
});

const usuarioVersao = computed(() => {
  return Number(permissionsService.getUser()?.versaoSistema || 0);
});

// Status of the loaded client: the archive button only makes sense for one that
// is not archived yet. Null until client-loaded arrives.
const clienteStatus = ref<number | null>(null);

const clienteId = computed(() => {
  return Number(modalClienteId.value || props.clienteId);
});

const handleClientLoaded = (clientData?: ClientLoadedData): void => {
  log('ClienteModal', 'handleClientLoaded', 'Client data received', clientData);

  if (clientData) {
    clienteStatus.value = Number(clientData.cliente_status) || null;
    clienteData.value.cliente_nome = clientData.cliente_nome || '';
    clienteData.value.cliente_franquia_id = clientData.cliente_franquia_id || null;
    clienteData.value.cliente_franquia_nome = clientData.franquia?.Name || '';
    clienteData.value.cliente_data_ultimo_orcamento = clientData.cliente_data_ultimo_orcamento || '';
    clienteData.value.cliente_data_ultimo_compromisso = clientData.cliente_data_ultimo_compromisso || '';
    clienteData.value.cliente_qtd_compromissos = clientData.cliente_qtd_compromissos || 0;
    clienteData.value.cliente_data_ultimo_contato_fidelizacao = clientData.cliente_data_ultimo_contato_fidelizacao || '';
  }
};

const handleClose = (): void => {
  log('ClienteModal', 'handleClose', 'Modal closed');

  isVisible.value = false;
  modalClienteId.value = 0;
  prefillData.value = null;
  pendingObservation.value = '';
  clienteStatus.value = null;
};

const handleSave = async (): Promise<void> => {
  log('ClienteModal', 'handleSave', 'Saving client');

  if (clienteFormRef.value) {
    isSaving.value = true;
    try {
      await clienteFormRef.value.save();
    } catch (err) {
      log('ClienteModal', 'handleSave', 'Save error', err);
    } finally {
      isSaving.value = false;
    }
  }
};

const handleSaveSuccess = (): void => {
  log('ClienteModal', 'handleSaveSuccess', 'Client saved successfully');
  emit('save-success');

  handleClose();
};

const handleArchive = (): void => {
  log('ClienteModal', 'handleArchive', 'Opening archive modal via eventBus');
  eventBus.emit('modal.cliente-archive.open', { id: clienteId.value });
};

const handleArchiveSuccess = (): void => {
  log('ClienteModal', 'handleArchiveSuccess', 'Client archived successfully');
  emit('save-success');
};

const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('ClienteModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    modalClienteId.value = Number(data.id);
    prefillData.value = null;
  } else {
    modalClienteId.value = 0;
    prefillData.value = (data?.prefillPhone || data?.prefillName)
      ? { phone: data.prefillPhone || '', name: data.prefillName || '' }
      : null;
  }
  isVisible.value = true;
  activeTab.value = 'editar';
  // Opening straight to the portal tab only makes sense when tab 5 actually
  // exists — a tenant without the portal feature has no such tab to land on.
  activeTabIndex.value = (data?.tab === 'portal' && portalClienteEnabled) ? 5 : 0;
};

const handleEditClienteEvent = (id?: ClienteId): void => {
  log('ClienteModal', 'handleEditClienteEvent', 'Received edit-cliente event', id);
  if (id === undefined) return;
  modalClienteId.value = Number(id);
  isVisible.value = true;
  activeTab.value = 'editar';
  activeTabIndex.value = 0;
};

const handleEditOrcamentoEvent = (orcamentoId?: ClienteId): void => {
  log('ClienteModal', 'handleEditOrcamentoEvent', 'Received orcamento.edit event, forwarding to modal.orcamentos.open', orcamentoId);
  eventBus.emit('modal.orcamentos.open', { id: orcamentoId });
};

watch(() => props.show, (newValue) => {
  if (newValue) {
    activeTab.value = 'editar';
    activeTabIndex.value = 0;
  }
});

watch(() => props.clienteId, (newValue) => {
  if (newValue > 0) {
    activeTab.value = 'editar';
    activeTabIndex.value = 0;
  }
});

// O estado do acesso ao portal é lido ao abrir o modal (e ao trocar de cliente),
// não na montagem: o ClienteModal fica montado o tempo todo no ModalManager.
watch([isVisible, clienteId], ([visivel, id]) => {
  if (!portalClienteEnabled) return;
  if (visivel && id > 0) {
    loadAcessoPortal(id);
  } else {
    resetAcessoPortal();
  }
});

watch(activeTabIndex, async (newIndex) => {
  const indexToTabMap: Array<'editar' | 'orcamentos' | 'contatos' | 'so-orders' | 'invoices'> = ['editar', 'orcamentos', 'contatos', 'so-orders', 'invoices'];
  activeTab.value = indexToTabMap[newIndex] || 'editar';

  await nextTick();

  if (newIndex === 1) {
    orcamentosTabRef.value?.loadData();
  }
  if (newIndex === 3) {
    soOrdersTabRef.value?.loadData();
  }
  if (newIndex === 4) {
    invoicesTabRef.value?.loadData();
  }
  if (newIndex === 6) {
    combosTabRef.value?.loadData();
  }
});

onMounted(() => {
  log('ClienteModal', 'mounted', 'Component mounted');

  eventBus.on('modal.cliente.open', handleModalOpen);
  eventBus.on('modal.cliente.close', handleClose);

  eventBus.on('cliente.archived', handleArchiveSuccess);

  eventBus.on('cliente.edit', handleEditClienteEvent);
  eventBus.on('orcamento.edit', handleEditOrcamentoEvent);
});

onBeforeUnmount(() => {
  log('ClienteModal', 'beforeUnmount', 'Component unmounting');

  eventBus.off('modal.cliente.open', handleModalOpen);
  eventBus.off('modal.cliente.close', handleClose);

  eventBus.off('cliente.archived', handleArchiveSuccess);

  eventBus.off('cliente.edit', handleEditClienteEvent);
  eventBus.off('orcamento.edit', handleEditOrcamentoEvent);
});
</script>

<style scoped>
.version-1-fields {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.modal-content-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.modal-left-column {
  flex: 1 1 calc(50% - 10px);
  min-width: 300px;
}

.modal-right-column {
  flex: 1 1 calc(50% - 10px);
  min-width: 300px;
}

.cliente-top-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

/* Inside the "Portal do cliente" tab, so it reads as panel content rather than
   the right-aligned strip it was when it floated above the tab bar. */
.portal-acesso-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  padding: 4px 0;
}

@media (max-width: 768px) {
  .modal-content-row {
    flex-direction: column;
  }
  
  .modal-left-column,
  .modal-right-column {
    flex: 1 1 100%;
  }
}

:deep(.p-tabs .p-tablist) {
  background: transparent;
  border-bottom: 2px solid #dee2e6;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* Hide scroll navigation buttons */
:deep(.p-tablist-nav-button),
:deep(.p-tablist-next-button),
:deep(.p-tablist-prev-button) {
  display: none !important;
}

:deep(.p-tabs .p-tab) {
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  white-space: nowrap;
}

:deep(.p-tabs .p-tabpanels) {
  background: transparent;
  padding: 0;
}

:deep(.p-tabs .p-tabpanel) {
  padding-top: 1rem;
}
</style>
